"use client";

import { useState } from "react";
import { Zap } from "lucide-react";

import { useQuotes } from "@/app/_module/hooks/useQuoteService";
import { CarrierLogo } from "@/app/_module/components/CarrierLogo";

// Default number of carrier tiles shown before "Show all". Cheapest-first, so
// the top of the list is what matters; the rest is one click away.
const DEFAULT_CAP = 20;

/**
 * Renders the carrier result boxes for a Compulife quote, reading from the
 * shared `useQuotes(leadId)` cache that the Compulife form invalidates on
 * submit. Used by BOTH the InsuraBot Compulife card AND the QuoteForm 403
 * fallback, so results always show regardless of which path produced them.
 *
 * Shows the agent's own carriers when they have any; otherwise falls back to
 * the suggested carriers so the boxes ALWAYS render (an agent with no carriers
 * configured still sees priced options, flagged as suggestions).
 */
export default function CompulifeQuoteResults({ leadId }: { leadId: string }) {
  const { data: quotes = [] } = useQuotes(leadId);
  const [showAll, setShowAll] = useState(false);
  const all = quotes as any[];
  const matched = all.filter((q) => !q?.isSuggested);
  const isSuggestedOnly = matched.length === 0 && all.length > 0;
  const rows = (matched.length > 0 ? matched : all)
    .slice()
    .sort(
      (a: any, b: any) =>
        (a.monthlyPremium ?? Infinity) - (b.monthlyPremium ?? Infinity),
    );
  if (rows.length === 0) return null;
  const cheapest = rows[0]?.id; // sorted cheapest-first → the Best Value tile
  const visible = showAll ? rows : rows.slice(0, DEFAULT_CAP);
  const hiddenCount = rows.length - visible.length;
  return (
    // min-w-0 + max-w-full: lets the scroll row below actually scroll instead
    // of forcing this wrapper (and the whole card) to grow to fit every tile.
    <div className="border-t mt-3 pt-3 min-w-0 max-w-full">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
          {showAll ? rows.length : `Top ${visible.length}`} of {rows.length}{" "}
          carrier{rows.length === 1 ? "" : "s"} · cheapest first
          {isSuggestedOnly && (
            <span className="ml-1.5 normal-case font-normal text-amber-600">
              · suggested (add these carriers to quote them directly)
            </span>
          )}
        </p>
        {hiddenCount > 0 && !showAll && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="text-[11px] font-semibold text-primary hover:underline flex-shrink-0"
          >
            Show all {rows.length}
          </button>
        )}
      </div>
      <div
        className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1"
        style={{ scrollbarWidth: "thin" }}
      >
        {visible.map((q: any) => (
          <CompulifeTile key={q.id} q={q} isBest={q.id === cheapest} />
        ))}
      </div>
    </div>
  );
}

function CompulifeTile({ q, isBest }: { q: any; isBest: boolean }) {
  const monthly =
    typeof q.monthlyPremium === "number" ? q.monthlyPremium : undefined;
  const annual =
    typeof q.annualPremium === "number" ? q.annualPremium : undefined;
  return (
    <div
      className={`flex-shrink-0 w-[200px] rounded-xl p-4 flex flex-col transition-all duration-200 hover:-translate-y-0.5 ${
        isBest
          ? "bg-gradient-to-br from-emerald-50 via-emerald-50/70 to-emerald-100/60 ring-2 ring-emerald-400/50"
          : "bg-white"
      }`}
      style={{
        boxShadow: isBest
          ? "rgba(16,185,129,0.25) 0px 0px 0px 1px, rgba(16,185,129,0.15) 0px 10px 28px -6px"
          : "rgba(0,0,0,0.05) 0px 0px 0px 1px, rgba(0,0,0,0.03) 0px 4px 12px -2px",
      }}
    >
      {isBest && (
        <div className="flex items-center gap-1 mb-2.5 -mt-0.5">
          <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-white bg-emerald-600 px-2 py-0.5 rounded shadow-sm shadow-emerald-600/30">
            <Zap className="h-2.5 w-2.5 fill-current" />
            Best Value
          </span>
        </div>
      )}
      <div className="flex items-center gap-2 mb-3">
        <CarrierLogo name={q.carrier} size={32} />
        <div className="min-w-0 flex-1">
          {/* title= so a truncated carrier / product name shows in full on hover */}
          <p
            className="text-[12px] font-bold text-gray-900 truncate leading-tight"
            title={q.carrier}
          >
            {q.carrier}
          </p>
          <p
            className="text-[10px] text-gray-500 truncate leading-tight mt-0.5"
            title={q.product}
          >
            {q.product}
          </p>
        </div>
      </div>
      <div className="mb-1">
        <div className="flex items-baseline gap-1">
          <span
            className={`text-3xl font-black leading-none tabular-nums tracking-tight ${
              isBest ? "text-emerald-700" : "text-gray-900"
            }`}
            style={{ fontFeatureSettings: '"tnum"' }}
          >
            ${monthly?.toFixed(2) ?? "—"}
          </span>
          <span className="text-[11px] text-gray-400 font-medium">/mo</span>
        </div>
        {annual != null && (
          <p className="text-[11px] text-gray-500 mt-1">
            ${Math.round(annual).toLocaleString()}/yr
          </p>
        )}
      </div>
      {q.amBestRating && (
        <p className="text-[10px] text-gray-400 mt-auto pt-2">
          AM Best {q.amBestRating}
        </p>
      )}
    </div>
  );
}
